import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import styles from 'feature/main/index.module.sass';
import CardItemGroup from 'components/card-item-group';
import ButtonDialog from 'components/button-dialog';
import iconShortcut from 'assets/icon_shortcut.svg';
import iconRefueling from 'assets/main-refueling.svg';
import iconParking from 'assets/main-parking.svg';
import iconOnStreetParking from 'assets/main-on-street-parking.svg';
import iconScanPay from 'assets/main-scan-payment.svg';
import iconArrow from 'assets/icon_arrow_blue.svg';
import iconSetting from 'assets/icon_action.svg';
import iconPromotions from 'assets/icon_promotions.svg';
import iconHelpCenter from 'assets/icon_helpcenter.svg';
import iconCreditCard from 'assets/icon_creditcard.svg';
import iconCheck from 'assets/icon_check.svg';
import iconPlateNumber from 'assets/icon_plate_number.svg';
import iconReceipt from 'assets/icon_receipt.svg';
import {
  useAppContext,
  SHOW_TOAST_MESSAGE,
  NOW_SETTING_LICENSE_PLATE_AND_CREDIT_CARD,
  NOW_SETTING_LICENSE_PLATE,
  NOW_SETTING_CREDIT_CARD,
  SET_CUSTOMER_SERVICE_DIALOG,
  SET_LINE_BANK_LOGIN_DIALOG,
} from 'use-app';
import { useMainContext, SET_LICENSE_PLATES_DIALOG, SET_CREDIT_CARD_DIALOG } from 'feature/main/use-main';
import { getLoginState } from 'model/user';
import amplitude from 'service/amplitude';
import appLoader from 'distributor/app-loader';
import PowerBy from 'components/power-by';
import ParkingRecords from './parking-records';
import OilPricing from './oil-pricing';
import Promotions from './promotions';
import { openLink } from 'helpers/link-helper';
import { getUserInfo } from 'helpers/data-check-helper';
import ShortcutHint from 'components/shortcut-hint';
import Notification from './notification';
import { PATH, introPromotionUrl, config, isCathaymr, isNpcpay, isCpcpay, isLineBank } from 'config';

const Main = () => {
  const { state: appState, dispatch: appDispatch, fetchMainPageData } = useAppContext();
  const { state, dispatch } = useMainContext();
  const { configData } = appLoader;
  const { mainFeaturesList } = configData;
  const history = useHistory();
  const { hasLicensePlates, hasPayments } = getUserInfo(appState);
  const loginState = getLoginState();
  const isUser = loginState === 'user';

  useEffect(() => {
    amplitude.main_pageview();
  }, []);

  useEffect(() => {
    if (!appState.mainPageIsSetted) {
      fetchMainPageData();
    }
  }, [appState.mainPageIsSetted]); //eslint-disable-line

  const setLicensePlateAndCreditCard = () => {
    if (isLineBank) {
      return (window.location.href = config.lineBankLicensePlatesPage);
    }

    appDispatch({
      type: NOW_SETTING_LICENSE_PLATE_AND_CREDIT_CARD,
      payload: {
        noPlateNoCard: true,
        from: 'main',
      },
    });
    history.push(PATH.LICENSE_PLATES_NEW);
  };

  const setLicensePlate = () => {
    if (isLineBank) {
      return (window.location.href = config.lineBankLicensePlatesPage);
    }

    appDispatch({
      type: NOW_SETTING_LICENSE_PLATE,
      payload: {
        noPlate: true,
        from: 'main',
      },
    });
    history.push(PATH.LICENSE_PLATES_NEW);
  };

  const licensePlatesDialogHide = () => {
    dispatch({
      type: SET_LICENSE_PLATES_DIALOG,
      payload: false,
    });
  };

  const creditCardDialogHide = () => {
    dispatch({
      type: SET_CREDIT_CARD_DIALOG,
      payload: false,
    });
  };

  const setCreditCard = () => {
    appDispatch({
      type: NOW_SETTING_CREDIT_CARD,
      payload: {
        noCard: true,
        from: 'main',
      },
    });
    history.push(PATH.MOCHIPAY_FLOW_SETTING);
  };

  const setLineBankLoginDialogOpen = () => {
    appDispatch({ type: SET_LINE_BANK_LOGIN_DIALOG });
  };

  const featuresItems = {
    refueling: {
      image: iconRefueling,
      content: '我要加油',
      onClick: () => {
        amplitude.main_refueling_onClick();
        history.push({ pathname: PATH.SERVICE_INSTRUCTION, search: '?service=refueling' });
      },
    },
    parking: {
      image: iconParking,
      content: '我要停車',
      onClick: () => {
        amplitude.main_parking_onClick();
        history.push({ pathname: PATH.SERVICE_INSTRUCTION, search: '?service=parking' });
      },
    },
    'on-street-parking': {
      image: iconOnStreetParking,
      content: '路邊停車',
      onClick: () => {
        amplitude.main_onStreetParking_onClick();
        navigateTo(PATH.STREET_PARKING_AUTOPAY);
      },
    },
    'barcode-payment': {
      image: iconScanPay,
      content: '條碼付款',
      onClick: () => {
        amplitude.main_scanpay_onClick();
        navigateTo('', () => {
          if (!hasPayments) {
            dispatch({
              type: SET_CREDIT_CARD_DIALOG,
              payload: true,
            });
          } else {
            history.push(PATH.SCAN_PAY);
          }
        });
      },
    },
  };

  const receiptHint = () => {
    if (isUser) {
      switch (appState.me?.receipt.type) {
        case 'carrier_member':
          return '會員載具';
        case 'carrier_mobile_barcode':
          return '手機條碼載具';
        case 'donate':
          return '發票捐贈';
        case 'b2b':
          return '三聯式電子發票';
        case 'carrier_citizen_digital':
          return '自然人憑證載具';
        default:
          return '會員載具';
      }
    } else {
      return '';
    }
  };

  const navigateTo = (path, callback) => {
    const linkToLogin = () => {
      appDispatch({
        type: SHOW_TOAST_MESSAGE,
        payload: '請先登入再繼續',
      });
      history.push(PATH.USER_LOGIN);
    };

    if (isUser) {
      if (callback) {
        callback();
      } else {
        history.push(path);
      }
    } else {
      if (isLineBank) {
        setLineBankLoginDialogOpen();
      } else {
        linkToLogin();
      }
    }
  };

  const firstItems = [
    {
      id: 'license-plates',
      icon: iconPlateNumber,
      content: '付款車牌',
      alert: isUser && !hasLicensePlates,
      rightContent: isUser && get(appState, 'licensePlates', []).length,
      withArrow: true,
      onClick: () => {
        amplitude.main_plateNumber_onClick();
        navigateTo(PATH.LICENSE_PLATES_INDEX);
      },
    },
    {
      id: 'mochi-pay-flow',
      icon: iconCreditCard,
      content: '付款方式',
      alert: isUser && !hasPayments,
      withArrow: true,
      onClick: () => {
        amplitude.main_payment_onClick();
        navigateTo(PATH.MOCHIPAY_FLOW_SETTING);
      },
    },
    {
      id: 'receipt',
      icon: iconReceipt,
      content: '發票設定',
      rightContent: receiptHint(),
      withArrow: true,
      onClick: () => {
        amplitude.main_receipt_onClick();
        navigateTo(PATH.RECEIPT_INDEX);
      },
    },
  ];

  const secondItems = [
    {
      id: 'promotions',
      icon: iconPromotions,
      content: '優惠總覽',
      withArrow: true,
      onClick: () => {
        amplitude.main_promotions_onClick();
        openLink(introPromotionUrl);
      },
    },
    {
      id: 'shortcut',
      icon: iconShortcut,
      content: '儲存網頁 App 捷徑',
      withArrow: true,
      onClick: () => {
        amplitude.main_shortcut_onClick();
        history.push(PATH.SHORTCUT_INSTRUCTION);
      },
    },
    {
      id: 'contact',
      icon: iconHelpCenter,
      content: '聯絡客服',
      onClick: () => {
        amplitude.main_contact_onClick();
        appDispatch({
          type: SET_CUSTOMER_SERVICE_DIALOG,
          payload: true,
        });
      },
    },
  ];

  const WelcomeMessage = () => {
    const message = isUser ? '歡迎使用麻吉行車付款' : '您尚未登入';

    const SettingButton = () => {
      return (
        <div
          className={styles.setting}
          onClick={() => {
            amplitude.main_setting_onClick();
            history.push(PATH.SETTINGS);
          }}
        >
          <img src={iconSetting} alt="iconSetting" />
        </div>
      );
    };

    return (
      <div className={styles['welcome-message']}>
        {message}
        {!isLineBank && <SettingButton />}
      </div>
    );
  };

  const BindingMessage = () => {
    const LoginMessage = () => {
      const onCompleteSetting = () => {
        amplitude.main_set_mochipay_onClick();
        if (!hasLicensePlates) {
          dispatch({
            type: SET_LICENSE_PLATES_DIALOG,
            payload: true,
          });
        } else if (!hasPayments) {
          dispatch({
            type: SET_CREDIT_CARD_DIALOG,
            payload: true,
          });
        }
      };
      if (hasPayments && hasLicensePlates) {
        return (
          <div className={styles['binding-message']}>
            <img src={iconCheck} alt="check" />
            已完成設定，可以前往付款。
          </div>
        );
      } else {
        return (
          <div className={`${styles['binding-message']} ${styles.set}`} onClick={onCompleteSetting}>
            完成麻吉付設定，啟動快速付款功能
            <img src={iconArrow} alt="arrow" />
          </div>
        );
      }
    };

    const NeedLoginMessage = () => {
      return (
        <div
          className={`${styles['binding-message']} ${styles.set}`}
          onClick={() => {
            amplitude.main_login_onClick();
            if (isLineBank) {
              setLineBankLoginDialogOpen();
            } else if (isNpcpay) {
              history.push(PATH.USER_REGISTER);
            } else {
              history.push(PATH.USER_LOGIN);
            }
          }}
        >
          立即登入/註冊帳號使用麻吉付
          <img src={iconArrow} alt="arrow" />
        </div>
      );
    };

    return isUser ? <LoginMessage /> : <NeedLoginMessage />;
  };

  const MainFeatures = () => {
    return (
      <ul className={styles['main-features']}>
        {mainFeaturesList.map(name => {
          let feature = featuresItems[name];
          return (
            <li className={styles.feature} onClick={feature.onClick} key={name}>
              <div className={styles.icon}>
                <img src={feature.image} alt="icon" />
              </div>
              {feature.content}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={styles.main}>
      <Notification />
      <ButtonDialog
        isShow={state.setLicensePlatesDialog}
        title={!hasPayments ? '簡單兩步驟，開通麻吉付' : '差一點就完成'}
        horizonal
        items={[
          {
            content: !hasPayments ? '立即開通麻吉付' : '立即設定付款車牌',
            theme: 'positive',
            onClick: !hasPayments ? setLicensePlateAndCreditCard : setLicensePlate,
          },
          {
            content: '稍後',
            theme: 'cancel',
            onClick: licensePlatesDialogHide,
          },
        ]}
      >
        {!hasPayments
          ? `完成付款方式及車牌設定，立即開通麻吉付，享受便捷的行動支付功能！`
          : `完成付款車牌設定，立即開通麻吉付，享受便捷的行動支付功能！`}
      </ButtonDialog>
      <ButtonDialog
        isShow={state.setCreditCardDialog}
        title="差一點就完成"
        horizonal
        items={[
          {
            content: '立即設定付款方式',
            theme: 'positive',
            onClick: setCreditCard,
          },
          {
            content: '稍後',
            theme: 'cancel',
            onClick: creditCardDialogHide,
          },
        ]}
      >
        完成付款方式設定，立即開通麻吉付，享受便捷的行動支付功能！
      </ButtonDialog>

      <section>
        <div className={styles.top}>
          <WelcomeMessage />
          <BindingMessage />
          <MainFeatures />
          <Promotions />
          <ParkingRecords />
        </div>
      </section>
      {appState.oilPricing && !(isCpcpay || isNpcpay) && (
        <section>
          <OilPricing />
        </section>
      )}
      <section>
        <div className={styles.list}>
          <CardItemGroup data={isNpcpay ? firstItems.filter(x => x.id !== 'on-street-parking') : firstItems} />
        </div>
      </section>
      <section>
        <div className={styles.list}>
          <CardItemGroup data={!isCathaymr ? secondItems.filter(x => x.id !== 'shortcut') : secondItems} />
        </div>
      </section>
      <div className={styles.footer}>
        <PowerBy />
      </div>
      {isCathaymr && <ShortcutHint />}
    </div>
  );
};

export default Main;
